/* eslint-disable */
import { useCallback, useState } from 'react';
import { useURLState } from './useURLState';
import { useStepValidation } from './useStepValidation';

/**
 * Initial enriched data structure
 * Stores unit labels locally for display in step wizard tags
 */
const initialEnrichedData = {
  tankVolumeUnitName: null,
  areaUnitName: null,
  rowSpacingUnitName: null,
  bandWidthUnitName: null,
  waterVolumeUnitName: null,
  productNames: {},
  productDoseUnitNames: {},
};

/**
 * Main composed hook for Spray Calculator
 * Combines URL state, validation, and enriched data for unit labels
 *
 * Unlike the Risk Calculator, the Spray Calculator uses simple numeric inputs
 * rather than cascading selects, so we don't need useEnrichedData or useAutoClear.
 * Unit labels are stored locally when the user selects them.
 *
 * @returns {Object} Complete Spray Calculator state and actions
 */
export function useSprayCalculator() {
  // URL state management
  const {
    urlState,
    setUrlState,
    currentStep,
    currentView,
    selections,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    goToResults,
    goToForm,
    resetUrlState,
    isFirstStep,
    isLastStep,
    getShareableURL,
  } = useURLState();

  // Step validation
  const { isStepValid, canProceedToNextStep, canCalculate } = useStepValidation(
    selections,
    currentStep,
    setUrlState
  );

  // Enriched data for unit labels (stored locally, not from URL)
  const [enrichedData, setEnrichedData] = useState(initialEnrichedData);

  // Results state (not stored in URL)
  const [results, setResults] = useState(null);

  // --- Step 1: Sprayer setters ---
  const setTankVolume = useCallback(
    (value) => {
      setUrlState({ tankVolume: String(value) });
    },
    [setUrlState]
  );

  const setTankVolumeUnitId = useCallback(
    (unitId, unitName = null) => {
      setUrlState({ tankVolumeUnitId: unitId });
      if (unitName) {
        setEnrichedData((prev) => ({ ...prev, tankVolumeUnitName: unitName }));
      }
    },
    [setUrlState]
  );

  // --- Step 2: Area setters ---
  const setTotalArea = useCallback(
    (value) => {
      setUrlState({ totalArea: String(value) });
    },
    [setUrlState]
  );

  const setAreaUnitId = useCallback(
    (unitId, unitName = null) => {
      setUrlState({ areaUnitId: unitId });
      if (unitName) {
        setEnrichedData((prev) => ({ ...prev, areaUnitName: unitName }));
      }
    },
    [setUrlState]
  );

  const setApplicationType = useCallback(
    (type) => {
      setUrlState({ applicationType: type });
    },
    [setUrlState]
  );

  const setBandPercentage = useCallback(
    (value) => {
      setUrlState({ bandPercentage: String(value) });
    },
    [setUrlState]
  );

  const setRowSpacing = useCallback(
    (value) => {
      setUrlState({ rowSpacing: String(value) });
    },
    [setUrlState]
  );

  const setRowSpacingUnitId = useCallback(
    (unitId, unitName = null) => {
      setUrlState({ rowSpacingUnitId: unitId });
      if (unitName) {
        setEnrichedData((prev) => ({ ...prev, rowSpacingUnitName: unitName }));
      }
    },
    [setUrlState]
  );

  const setBandWidth = useCallback(
    (value) => {
      setUrlState({ bandWidth: String(value) });
    },
    [setUrlState]
  );

  const setBandWidthUnitId = useCallback(
    (unitId, unitName = null) => {
      setUrlState({ bandWidthUnitId: unitId });
      if (unitName) {
        setEnrichedData((prev) => ({ ...prev, bandWidthUnitName: unitName }));
      }
    },
    [setUrlState]
  );

  // --- Step 3: Treatment setters ---
  const setWaterVolume = useCallback(
    (value) => {
      setUrlState({ waterVolume: String(value) });
    },
    [setUrlState]
  );

  const setWaterVolumeUnitId = useCallback(
    (unitId, unitName = null) => {
      setUrlState({ waterVolumeUnitId: unitId });
      if (unitName) {
        setEnrichedData((prev) => ({ ...prev, waterVolumeUnitName: unitName }));
      }
    },
    [setUrlState]
  );

  const setProducts = useCallback(
    (products, enrichedProducts = null) => {
      setUrlState({ products });
      if (enrichedProducts) {
        setEnrichedData((prev) => ({
          ...prev,
          productNames: enrichedProducts.reduce((acc, p) => {
            if (p.id && p.name) acc[p.id] = p.name;
            return acc;
          }, {}),
          productDoseUnitNames: enrichedProducts.reduce((acc, p) => {
            if (p.id && p.unitName) acc[p.id] = p.unitName;
            return acc;
          }, {}),
        }));
      }
    },
    [setUrlState]
  );

  // Full reset
  const reset = useCallback(() => {
    resetUrlState();
    setEnrichedData(initialEnrichedData);
    setResults(null);
  }, [resetUrlState]);

  return {
    // State
    currentStep,
    currentView,
    selections,
    enrichedData,
    results,

    // Navigation actions
    goToStep,
    goToNextStep,
    goToPreviousStep,

    // View actions
    goToResults,
    goToForm,

    // Step 1 setters
    setTankVolume,
    setTankVolumeUnitId,

    // Step 2 setters
    setTotalArea,
    setAreaUnitId,
    setApplicationType,
    setBandPercentage,
    setRowSpacing,
    setRowSpacingUnitId,
    setBandWidth,
    setBandWidthUnitId,

    // Step 3 setters
    setWaterVolume,
    setWaterVolumeUnitId,
    setProducts,
    setResults,
    reset,

    // Validation
    isStepValid,
    canProceedToNextStep,
    canCalculate,

    // Helper flags
    isFirstStep,
    isLastStep,

    // URL sharing
    getShareableURL,
  };
}

export default useSprayCalculator;
